import { useState, useEffect } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import { Add as AddIcon, Search as SearchIcon } from '@material-ui/icons';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import { getTransitionWithVariable } from '../../../redux_store';
import { SURVEY_STATUS } from './constants';

const useStyles = makeStyles((theme) => ({
  container: {
    background: '#f8f9fa',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0, // Important for flex children
  },
  searchContainer: {
    padding: theme.spacing(2.5, 2.5, 0, 2.5),
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
    background: '#f8f9fa',
    position: 'relative',
    zIndex: 2,
  },
  searchWrapper: {
    display: 'flex',
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(2),
  },
  searchInputWrapper: {
    position: 'relative',
    flex: 1,
  },
  addButton: {
    width: 44,
    height: 44,
    minWidth: 44,
    borderRadius: 8,
    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s',
    fontSize: 24,
    fontWeight: 300,
    boxShadow: '0 2px 8px rgba(72, 187, 120, 0.2)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(72, 187, 120, 0.3)',
    },
  },
  searchInput: {
    width: '100%',
    padding: theme.spacing(1.5, 1.5, 1.5, 5),
    background: 'white',
    border: '1px solid #e1e4e8',
    borderRadius: 8,
    fontSize: 14,
    transition: 'all 0.3s ease',
    '&:focus': {
      outline: 'none',
      borderColor: '#667eea',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
    },
    '&::placeholder': {
      color: '#999',
    },
  },
  searchIcon: {
    position: 'absolute',
    left: theme.spacing(1.5),
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#999',
    fontSize: 20,
  },
  surveysList: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2.5, 2.5, 0, 2.5),
    minHeight: 0, // Allow shrinking
    maxHeight: '100%', // Ensure it doesn't exceed container
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: 3,
      '&:hover': {
        background: 'rgba(0, 0, 0, 0.3)',
      },
    },
  },
  highlightText: {
    background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
    color: '#856404',
    padding: '1px 3px',
    borderRadius: 3,
    fontWeight: 600,
    boxShadow: '0 1px 3px rgba(133, 100, 4, 0.2)',
  },
  surveyItem: {
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(1.5),
    backgroundColor: '#ffffff',
    borderRadius: 12,
    border: '1px solid #e8eaed',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: 4,
      height: '100%',
      backgroundColor: 'transparent',
      transition: 'background-color 0.3s ease',
    },
    '&:hover': {
      transform: 'translateY(-2px)',
    },
    '&.draft': {
      '&::before': {
        backgroundColor: '#f0ad4e',
      },
      '&:hover': {
        borderColor: '#f0ad4e',
        boxShadow: '0 4px 20px rgba(240, 173, 78, 0.15)',
        '&::before': {
          backgroundColor: '#f0ad4e',
        },
      },
      '&.selected': {
        borderColor: '#f0ad4e',
        backgroundColor: 'rgba(240, 173, 78, 0.04)',
        boxShadow: '0 4px 16px rgba(240, 173, 78, 0.2)',
        '&::before': {
          backgroundColor: '#f0ad4e',
        },
      },
    },
    '&.active': {
      '&::before': {
        backgroundColor: '#5cb85c',
      },
      '&:hover': {
        borderColor: '#5cb85c',
        boxShadow: '0 4px 20px rgba(92, 184, 92, 0.15)',
        '&::before': {
          backgroundColor: '#5cb85c',
        },
      },
      '&.selected': {
        borderColor: '#5cb85c',
        backgroundColor: 'rgba(92, 184, 92, 0.04)',
        boxShadow: '0 4px 16px rgba(92, 184, 92, 0.2)',
        '&::before': {
          backgroundColor: '#5cb85c',
        },
      },
    },
    '&.inactive': {
      opacity: 0.7,
      '&::before': {
        backgroundColor: '#d9534f',
      },
      '&:hover': {
        opacity: 0.9,
        borderColor: '#d9534f',
        boxShadow: '0 4px 20px rgba(217, 83, 79, 0.15)',
        '&::before': {
          backgroundColor: '#d9534f',
        },
      },
      '&.selected': {
        opacity: 1,
        borderColor: '#d9534f',
        backgroundColor: 'rgba(217, 83, 79, 0.04)',
        boxShadow: '0 4px 16px rgba(217, 83, 79, 0.2)',
        '&::before': {
          backgroundColor: '#d9534f',
        },
      },
    },
    '&.archived': {
      opacity: 0.5,
      '&::before': {
        backgroundColor: '#6c757d',
      },
      '&:hover': {
        opacity: 0.7,
        borderColor: '#6c757d',
        boxShadow: '0 4px 20px rgba(108, 117, 125, 0.15)',
        '&::before': {
          backgroundColor: '#6c757d',
        },
      },
      '&.selected': {
        opacity: 1,
        borderColor: '#6c757d',
        backgroundColor: 'rgba(108, 117, 125, 0.04)',
        boxShadow: '0 4px 16px rgba(108, 117, 125, 0.2)',
        '&::before': {
          backgroundColor: '#6c757d',
        },
      },
    },
  },
  surveyTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1a1a1a',
    lineHeight: 1.3,
    flex: 1,
  },
  surveyType: {
    fontSize: '0.875rem',
    color: '#1976d2',
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    backgroundColor: 'rgba(25, 118, 210, 0.08)',
    padding: '4px 8px',
    borderRadius: 6,
    display: 'inline-block',
  },
  surveyDate: {
    fontSize: '0.8125rem',
    color: '#666',
    lineHeight: 1.4,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  emptyIcon: {
    fontSize: 48,
    opacity: 0.3,
    marginBottom: theme.spacing(2),
  },
  emptyText: {
    fontSize: '0.875rem',
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  surveyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.75),
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  badgeNew: {
    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
    color: 'white',
    animation: '$pulse 2s infinite',
    boxShadow: '0 2px 6px rgba(72, 187, 120, 0.3)',
  },
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 2px 6px rgba(72, 187, 120, 0.3)',
    },
    '50%': {
      boxShadow: '0 2px 10px rgba(72, 187, 120, 0.5)',
    },
    '100%': {
      boxShadow: '0 2px 6px rgba(72, 187, 120, 0.3)',
    },
  },
  statusFilterContainer: {
    paddingBottom: theme.spacing(2.5),
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
  },
  statusFilterTabs: {
    display: 'flex',
    gap: 2,
    width: '100%',
    position: 'relative',
    padding: '2px 0',
    borderBottom: `1px solid ${theme.colors?.gray2 || '#E4E4E4'}`,
  },
  statusTab: {
    flex: 1,
    minHeight: 40,
    border: 'none',
    background: 'transparent',
    color: theme.colors?.gray4 || '#818FA0',
    fontSize: '0.8125rem',
    fontWeight: 400,
    textTransform: 'none',
    borderRadius: 0,
    transition: 'color 0.15s ease, border-color 0.15s ease',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    letterSpacing: '0.005em',
    padding: '12px 8px 11px 8px',
    borderBottom: '1px solid transparent',
    marginBottom: '-1px',
    '&:hover': {
      color: theme.colors?.blue2 || '#122056',
    },
    '&:focus': {
      outline: 'none',
    },
    '&.active': {
      color: theme.colors?.primary || '#1b71e6',
      fontWeight: 500,
      borderBottomColor: theme.colors?.primary || '#1b71e6',
    },
  },
  archivedTab: {
    '&:hover': {
      color: theme.colors?.blue2 || '#122056',
    },
    '&.active': {
      color: theme.colors?.grey || '#8E8E8E',
      fontWeight: 500,
      borderBottomColor: theme.colors?.grey || '#8E8E8E',
    },
  },
  statusIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0.25, 0.75),
    borderRadius: 12,
    fontSize: '0.65rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: theme.spacing(0.5),
    '&.draft': {
      backgroundColor: '#fff3cd',
      color: '#856404',
    },
    '&.active': {
      backgroundColor: '#d4edda',
      color: '#155724',
    },
    '&.inactive': {
      backgroundColor: '#f8d7da',
      color: '#721c24',
    },
    '&.archived': {
      backgroundColor: '#e2e3e5',
      color: '#383d41',
    },
  },
}));

const SurveysSidebar = ({ selectedSurvey, onSurveySelect, onAddSurvey, instantSurveys }) => {
  const classes = useStyles();
  const { en: { instantSurvey } } = useSelector((state) => state.i18n);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [previousSurveyKeys, setPreviousSurveyKeys] = useState(new Set());
  const [newSurveysTimestamps, setNewSurveysTimestamps] = useState(new Map()); // key -> timestamp when added
  const [isInitialized, setIsInitialized] = useState(false);

  // Get instant surveys from Redux store
  const {
    assessmentInstruments
  } = useSelector((state) => state.instantAssessment);

  // Initialize on first load - don't mark existing surveys as new
  useEffect(() => {
    if (!isInitialized && instantSurveys.length > 0) {
      const currentSurveyKeys = new Set(instantSurveys.map(survey => survey.key));
      setPreviousSurveyKeys(currentSurveyKeys);
      setIsInitialized(true);
    }
  }, [instantSurveys, isInitialized]);

  // Track new surveys when instantSurveys array changes (only after initialization)
  useEffect(() => {
    if (!isInitialized) return; // Don't track until initialized

    const currentSurveyKeys = new Set(instantSurveys.map(survey => survey.key));
    const now = Date.now();

    // Find newly added surveys
    currentSurveyKeys.forEach(key => {
      if (!previousSurveyKeys.has(key)) {
        // This is a new survey, mark it with current timestamp
        setNewSurveysTimestamps(prev => new Map([...prev, [key, now]]));
      }
    });

    setPreviousSurveyKeys(currentSurveyKeys);
  }, [instantSurveys, previousSurveyKeys, isInitialized]);

  // Auto-update current time every 5 seconds and cleanup old NEW badges
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();

      // Remove surveys older than 1 minute from newSurveysTimestamps
      setNewSurveysTimestamps(prev => {
        const updated = new Map();
        prev.forEach((timestamp, key) => {
          if (now - timestamp <= 60000) { // 1 minute = 60000ms
            updated.set(key, timestamp);
          }
        });
        return updated;
      });
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Helper function to get survey type name from ID
  const getSurveyTypeName = (surveyTypeId) => {
    const instrument = assessmentInstruments.find(inst => inst.id === surveyTypeId);
    return instrument ? instrument.name : surveyTypeId;
  };

  // Function to highlight matching text
  const highlightText = (text, query) => {
    if (!query.trim()) {
      return text;
    }

    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return (
      <span>
        {parts.map((part, index) => {
          const isMatch = regex.test(part);
          return isMatch ? (
            <span key={index} className={classes.highlightText}>
              {part}
            </span>
          ) : (
            <span key={index}>{part}</span>
          );
        })}
      </span>
    );
  };

  // Check if a survey is new (was recently added to the list)
  const isSurveyNew = (survey) => {
    return newSurveysTimestamps.has(survey.key);
  };

  const filteredSurveys = instantSurveys.filter(survey => {
    // Filter by search term
    const surveyTypeName = getSurveyTypeName(survey.surveyType);
    const matchesSearch = survey.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           surveyTypeName?.toLowerCase().includes(searchTerm.toLowerCase());

    // Filter by status
    const surveyStatus = survey.status || 'draft';

    // Hide archived surveys from "All" view - they should only be visible when explicitly filtering by "archived"
    if (statusFilter === 'all' && surveyStatus === SURVEY_STATUS.archived) {
      return false;
    }

    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === SURVEY_STATUS.draft && surveyStatus === SURVEY_STATUS.draft) ||
                         (statusFilter === SURVEY_STATUS.active && surveyStatus === SURVEY_STATUS.active) ||
                         (statusFilter === SURVEY_STATUS.inactive && surveyStatus === SURVEY_STATUS.inactive) ||
                         (statusFilter === SURVEY_STATUS.archived && surveyStatus === SURVEY_STATUS.archived);

    return matchesSearch && matchesStatus;
  });

  // Auto-select first survey when status filter changes
  useEffect(() => {
    if (filteredSurveys.length > 0 && onSurveySelect) {
      // Select the first survey in the filtered list
      onSurveySelect(filteredSurveys[0].key);
    } else if (filteredSurveys.length === 0 && onSurveySelect) {
      // Clear selection if no surveys match the filter
      onSurveySelect(null);
    }
  }, [statusFilter]); // Only run when statusFilter changes

  return (
    <div className={classes.container}>
      {/* Fixed Top Search with Add Button */}
      <div className={classes.searchContainer}>
        <div className={classes.searchWrapper}>
          <div className={classes.searchInputWrapper}>
            <SearchIcon className={classes.searchIcon} />
            <input
              type="text"
              placeholder={instantSurvey.searchPlaceholder}
              className={classes.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            type="button"
            className={classes.addButton}
            onClick={onAddSurvey}
            title={instantSurvey.addButtonTitle}
          >
            +
          </button>
        </div>

        {/* Status Filter Tabs */}
        <div className={classes.statusFilterContainer}>
          <div className={classes.statusFilterTabs}>
            <button
              type="button"
              className={cx(
                classes.statusTab,
                { active: statusFilter === 'all' }
              )}
              onClick={() => setStatusFilter('all')}
            >
              {instantSurvey.statusAll}
            </button>
            <button
              type="button"
              className={cx(
                classes.statusTab,
                { active: statusFilter === SURVEY_STATUS.draft }
              )}
              onClick={() => setStatusFilter(SURVEY_STATUS.draft)}
            >
              {instantSurvey.statusDraft}
            </button>
            <button
              type="button"
              className={cx(
                classes.statusTab,
                { active: statusFilter === SURVEY_STATUS.active }
              )}
              onClick={() => setStatusFilter(SURVEY_STATUS.active)}
            >
              {instantSurvey.statusActive}
            </button>
            <button
              type="button"
              className={cx(
                classes.statusTab,
                { active: statusFilter === SURVEY_STATUS.inactive }
              )}
              onClick={() => setStatusFilter(SURVEY_STATUS.inactive)}
            >
              {instantSurvey.statusInactive}
            </button>
            <button
              type="button"
              className={cx(
                classes.statusTab,
                classes.archivedTab,
                { active: statusFilter === SURVEY_STATUS.archived }
              )}
              onClick={() => setStatusFilter(SURVEY_STATUS.archived)}
            >
              {instantSurvey.statusArchived}
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Surveys List */}
      <div className={classes.surveysList}>
        {filteredSurveys.length === 0
          ? <div className={classes.emptyState}>
            <AddIcon className={classes.emptyIcon} />
            <Typography className={classes.emptyText}>
              {searchTerm ? instantSurvey.emptyStateNotFound : instantSurvey.emptyStateNoAssessments}
            </Typography>
          </div>
          : filteredSurveys.map((survey) => (
            <div
              key={survey.key}
              className={`${classes.surveyItem} ${selectedSurvey === survey.key ? 'selected' : ''} ${survey.status || SURVEY_STATUS.draft}`}
              onClick={() => onSurveySelect(survey.key)}
            >
              <div className={classes.surveyHeader}>
                <Typography className={classes.surveyTitle}>
                  {highlightText(survey.title, searchTerm)}
                </Typography>
                {isSurveyNew(survey) && (
                  <span className={`${classes.badge} ${classes.badgeNew}`}>
                    {instantSurvey.badgeNew}
                  </span>
                )}
              </div>
              <Typography className={classes.surveyType}>
                {highlightText(getSurveyTypeName(survey.surveyType), searchTerm)}
              </Typography>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                <Typography className={classes.surveyDate}>
                  {/*Code: {survey.code} • */}
                  {survey.maxSubmissions
                    ? getTransitionWithVariable(instantSurvey.submissionsWithLimit, {
                        current: survey.currentSubmissions,
                        max: survey.maxSubmissions
                      })
                    : getTransitionWithVariable(instantSurvey.submissionsWithoutLimit, {
                        current: survey.currentSubmissions
                      })
                  }
                </Typography>
                <span className={`${classes.statusIndicator} ${survey.status || SURVEY_STATUS.draft}`}>
                  {(survey.status || SURVEY_STATUS.draft) === SURVEY_STATUS.draft && instantSurvey.statusLabelDraft}
                  {(survey.status || SURVEY_STATUS.draft) === SURVEY_STATUS.active && instantSurvey.statusLabelActive}
                  {(survey.status || SURVEY_STATUS.draft) === SURVEY_STATUS.inactive && instantSurvey.statusLabelInactive}
                  {(survey.status || SURVEY_STATUS.draft) === SURVEY_STATUS.archived && instantSurvey.statusLabelArchived}
                </span>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
};

export default SurveysSidebar;
